import Vue from 'vue';
import UploadApi from '@/api/upload/UploadApi';
import SocialApi from '@/api/social/SocialApi';
import defaultSocialThumb from '@/styles/socialimage.png';
import axios from 'axios';

// IndexedDB 설정
const DB_NAME = 'uploadDB';
const STORE_NAME = 'files';
function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      db.createObjectStore(STORE_NAME, { keyPath: 'id' });
    };
  });
}
async function saveFileData(fileData) {

  // console.log("저장 => ", fileData);

  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  tx.objectStore(STORE_NAME).put(fileData);

  return tx.complete;
}
async function loadAllFileData() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}
async function removeFileData(id) {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  tx.objectStore(STORE_NAME).delete(id);

  return tx.complete;
}

// async function removeAllData() {
//   const db = await openDB();
//   const tx = db.transaction(STORE_NAME, 'readwrite');
//   tx.objectStore(STORE_NAME).clear();  // 전체 삭제

//   // console.log("삭제 확인 => ", tx);
//   return tx.complete;
// }

const STATUS_PROGRESS = {
  queued: 5,
  processing: 60,
  uploading: 60,
  paused: 10,
  completed: 100,
  done: 100,
  failed: 100,
};

const clampProgress = (value, status) => {
  const fallback = STATUS_PROGRESS[status];
  const numeric = Number(value);

  if (!Number.isFinite(numeric)) {
    return fallback !== undefined ? fallback : 0;
  }

  if (numeric <= 0 && fallback !== undefined) {
    return fallback;
  }

  return Math.max(0, Math.min(100, Math.round(numeric)));
};

const mapTaskToJob = (task) => {
  if (!task || typeof task !== 'object') return null;

  const integration = task.integration || {};
  const platform = integration.platform || {};
  const payload = task.payload || {};

  const title =
    payload.title ||
    integration.external_name ||
    platform.name ||
    '소셜 업로드';

  const thumb =
    payload.thumb ||
    payload.thumbnail ||
    payload.thumbnail_url ||
    integration.settings?.thumbnail_url ||
    defaultSocialThumb;

  const status = task.status || 'queued';
  const progress = clampProgress(
    task.progress_percent ?? task.progress,
    status
  );

  return {
    id: String(task.id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
    taskId: task.id ?? null,
    assetId: task.asset_id ?? null,
    title,
    platform: platform.name || platform.key || 'Unknown',
    progress,
    status,
    progressPercent: progress,
    showOnAccordion: task.show_on_accordion !== undefined ? Boolean(task.show_on_accordion) : true,
    thumb,
    error: task.error_message || null,
    response: task.response || null,
    createdAt: task.created_at || null,
    startedAt: task.started_at || null,
    finishedAt: task.finished_at || null,
  };
};

const normalizeTaskList = (tasks = []) => {
  if (!Array.isArray(tasks)) return [];
  return tasks
    .map((task) => mapTaskToJob(task))
    .filter((job) => job !== null);
};

const state = {
  uploadedFiles: [],
  fileProgress: {},
  lastCompletedFile: null,
  isModalOpen: false,
  socialJobs: [],
  socialPausedAll: false,
};

const getters = {
  uploadedFiles: state => state.uploadedFiles,
  fileProgress: state => state.fileProgress,
  lastCompletedFileName: state => state.lastCompletedFile,
  socialJobs: state => state.socialJobs,
  socialPausedAll: state => state.socialPausedAll,
};

const mutations = {
  ADD_UPLOADED_FILES(state, file) {
    const maxSizeInBytes = 10 * 1024 * 1024 * 1024; // 10GB
    const newFile = {
      ...file,
      // status: file.size > maxSizeInBytes ? 'overlimit' : 'pending',
      status: file.size > maxSizeInBytes ? 'overlimit' : file.status, // 'ready' 상태
    };
    state.uploadedFiles.push(newFile);
  },
  REMOVE_UPLOADED_FILE(state, fileId) {
    state.uploadedFiles = state.uploadedFiles.filter(file => file.id !== fileId);
  },
  SET_FILE_PROGRESS(state, { fileId, progress }) {
    const fileIndex = state.uploadedFiles.findIndex(file => file.id === fileId);
    if (fileIndex !== -1) {
      const file = state.uploadedFiles[fileIndex];
      file.progress = progress;
      file.status = progress < 100 ? 'uploading' : 'completed';
      state.uploadedFiles = [...state.uploadedFiles];
    }
  },
  INIT_FILE_PROGRESS(state, fileId) {
    state.fileProgress[fileId] = 0;
  },
  SET_FILE_PROGRESSING(state, fileId) {
    const fileIndex = state.uploadedFiles.findIndex(file => file.id === fileId);
    if (fileIndex !== -1) {
      Vue.set(state.uploadedFiles[fileIndex], 'status', 'progressing');
    }
  },
  SET_FILE_STATUS(state, { fileId, status }) {
    const fileIndex = state.uploadedFiles.findIndex(file => file.id === fileId);
    if (fileIndex !== -1) {
      Vue.set(state.uploadedFiles[fileIndex], 'status', status);
      if (status === 'completed') {
        state.lastCompletedFile = state.uploadedFiles[fileIndex].name;
      }
    }
  },
  SET_UPLOADED_FILES(state, files) {
    state.uploadedFiles = files;
  },
  REPLACE_SOCIAL_JOBS(state, jobs) {
    state.socialJobs = Array.isArray(jobs) ? jobs : [];
    if (!state.socialJobs.length) {
      state.socialPausedAll = false;
    }
  },
  UPSERT_SOCIAL_JOB(state, job) {
    if (!job || typeof job !== 'object') return;
    const normalized = {
      ...job,
    };
    normalized.progress = clampProgress(
      job.progress ?? job.progress_percent,
      job.status
    );
    normalized.progressPercent = normalized.progress;
    const index = state.socialJobs.findIndex(existing => existing.id === normalized.id);
    if (index === -1) {
      state.socialJobs = [...state.socialJobs, normalized];
    } else {
      const updated = { ...state.socialJobs[index], ...normalized };
      state.socialJobs = [
        ...state.socialJobs.slice(0, index),
        updated,
        ...state.socialJobs.slice(index + 1),
      ];
    }
  },
  REMOVE_SOCIAL_JOB(state, jobId) {
    state.socialJobs = state.socialJobs.filter(job => job.id !== jobId);
    if (!state.socialJobs.length) {
      state.socialPausedAll = false;
    }
  },
  CLEAR_SOCIAL_JOBS(state) {
    state.socialJobs = [];
    state.socialPausedAll = false;
  },
  SET_SOCIAL_JOB_PROGRESS(state, { jobId, progress }) {
    const index = state.socialJobs.findIndex(job => job.id === jobId);
    if (index === -1) return;
    const next = { ...state.socialJobs[index], progress };
    state.socialJobs = [
      ...state.socialJobs.slice(0, index),
      next,
      ...state.socialJobs.slice(index + 1),
    ];
  },
  SET_SOCIAL_JOB_STATUS(state, { jobId, status }) {
    const index = state.socialJobs.findIndex(job => job.id === jobId);
    if (index === -1) return;
    const progress = STATUS_PROGRESS[status];
    const next = {
      ...state.socialJobs[index],
      status,
      progress: progress !== undefined ? progress : state.socialJobs[index].progress,
    };
    state.socialJobs = [
      ...state.socialJobs.slice(0, index),
      next,
      ...state.socialJobs.slice(index + 1),
    ];
  },
  SET_SOCIAL_BULK_STATUS(state, status) {
    const progress = STATUS_PROGRESS[status];
    state.socialJobs = state.socialJobs.map(job => {
      if (['done', 'completed', 'failed'].includes(job.status)) {
        return job;
      }
      return {
        ...job,
        status,
        progress: progress !== undefined ? progress : job.progress,
      };
    });
  },
  SET_SOCIAL_PAUSED_ALL(state, paused) {
    state.socialPausedAll = paused;
  },
};

const actions = {
  async removeFinished({state}){
     state.uploadedFiles = state.uploadedFiles.filter(e => e.status !== 'completed');

    //  await removeAllData();
  },
  addUploadedFile({ commit }, file) {
    commit('ADD_UPLOADED_FILES', file);
  },
  async removeUploadedFile({ commit }, fileId) {
    commit('REMOVE_UPLOADED_FILE', fileId);

    await removeFileData(fileId);
  },
  setFileProgress({ commit }, { fileId, progress }) {
    commit('SET_FILE_PROGRESS', { fileId, progress });
  },
  initFileProgress({ commit }, fileId) {
    commit('INIT_FILE_PROGRESS', fileId);
  },
  addSocialJob({ commit }, job) {
    commit('UPSERT_SOCIAL_JOB', job);
  },
  updateSocialJob({ commit }, job) {
    commit('UPSERT_SOCIAL_JOB', job);
  },
  removeSocialJob({ commit }, jobId) {
    commit('REMOVE_SOCIAL_JOB', jobId);
  },
  async hideSocialJob({ commit }, jobId) {
    if (!jobId) return;
    try {
      await SocialApi.hidePublishTask(jobId);
      commit('REMOVE_SOCIAL_JOB', jobId);
    } catch (error) {
      console.error('[upload] Failed to hide social job', error);
      throw error;
    }
  },
  async hideAllSocialJobs({ commit }) {
    try {
      await SocialApi.hideAllPublishTasks();
      commit('CLEAR_SOCIAL_JOBS');
    } catch (error) {
      console.error('[upload] Failed to hide all social jobs', error);
      throw error;
    }
  },
  async cancelSocialJob({ commit }, jobId) {
    if (!jobId) return;
    try {
      await SocialApi.cancelPublishTask(jobId);
      commit('REMOVE_SOCIAL_JOB', jobId);
    } catch (error) {
      console.error('[upload] Failed to cancel social job', error);
      throw error;
    }
  },
  clearSocialJobs({ commit }) {
    commit('CLEAR_SOCIAL_JOBS');
  },
  setSocialJobsFromBackend({ commit }, tasks) {
    const jobs = normalizeTaskList(tasks);
    commit('REPLACE_SOCIAL_JOBS', jobs);
  },
  async fetchSocialJobs({ dispatch }) {
    try {
      const tasks = await SocialApi.fetchPublishTasks();
      dispatch('setSocialJobsFromBackend', tasks);
      return tasks;
    } catch (error) {
      console.error('[upload] Failed to fetch social jobs', error);
      return null;
    }
  },
  updateSocialJobProgress({ commit }, payload) {
    commit('SET_SOCIAL_JOB_PROGRESS', payload);
  },
  updateSocialJobStatus({ commit }, payload) {
    commit('SET_SOCIAL_JOB_STATUS', payload);
  },
  setSocialPausedAll({ commit }, paused) {
    commit('SET_SOCIAL_PAUSED_ALL', paused);
    commit('SET_SOCIAL_BULK_STATUS', paused ? 'paused' : 'uploading');
  },
  toggleSocialPausedAll({ state, dispatch }) {
    if (!state.socialJobs.length) return;
    dispatch('setSocialPausedAll', !state.socialPausedAll);
  },

  addFiles({ commit }, params) {
    const files = params.files;
    const validFiles = files.filter(file => {
      const type = file.name.split('.').pop().toLowerCase();
      if (type === 'mp4' || type === 'mov') {
        const fileTypeLower = file.type.toLowerCase();
        return fileTypeLower === 'video/mp4' || fileTypeLower === 'video/quicktime';
      }
      return null;
    }).map(file => ({
      name: file.name,
      size: file.size,
      type: file.type.split('/').pop().toUpperCase(),
      id: Date.now() + Math.random().toString(36).substring(2, 15),
      progress: 0,
      status: 'ready',
      file: file,
    }));

    validFiles.forEach(async file => {
      commit('ADD_UPLOADED_FILES', file);
    });
  },

  async uploadFiles({ dispatch, state, commit }, options) {
    try {
      const params =
        typeof options === 'object' && options !== null
          ? {
              secure: Boolean(options.secure),
              aiVoice: Boolean(options.aiVoice),
            }
          : {
              secure: Boolean(options),
              aiVoice: false,
            };

      const temp = state.uploadedFiles.filter(f => f.status === 'ready');
      for(const obj of temp){
        commit('SET_FILE_STATUS', { fileId: obj.id, status: 'pending' });
      }

      // console.log("업로드 파일 => ", state.uploadedFiles)

      for (let file of state.uploadedFiles.filter(f => f.status === 'pending')) {
        try {
           // UI 표시용 임시 저장
          await saveFileData({
            id: file.id,
            name: file.name,
            size: file.size,
            type: file.type,
            progress: 0,
            status: 'pending',
            fileBlob: file.file,
            secure: params.secure,
            aiVoice: params.aiVoice,
          });

          await dispatch('uploadFile', { file: file.file, fileIndex: file.id, secure: params.secure, aiVoice: params.aiVoice });
        } catch (error) {
          console.error('error', error);
          break;
        }
      }
    } catch (error) {
      console.error('error', error);
    }
  },

  async reUploadFiles({ dispatch, state, commit }) {

    const temp = state.uploadedFiles.filter(f => f.status === 'waitingToResume');
    for(const obj of temp){
      commit('SET_FILE_STATUS', { fileId: obj.id, status: 'resumed' });
    }

    for (let file of state.uploadedFiles.filter(f => f.status === 'resumed')) {
      try {
        await dispatch('uploadFile', { file: file.file, fileIndex: file.id, secure: file.secure, aiVoice: file.aiVoice });
      } catch (error) {
        console.error('error', error);
        break;
      }
    }
  },

  async uploadFile({ commit, dispatch, rootGetters}, { file, fileIndex, secure, aiVoice }) {
    if (!(file instanceof File)) return;

    const t = rootGetters['t'];

    commit('SET_FILE_STATUS', { fileId: fileIndex, status: 'uploading' });

    const chunkSize = 5 * 1024 * 1024;  
    const totalChunks = Math.ceil(file.size / chunkSize);
    let isCompleted = true;

    try {
      if (!sessionStorage.getItem('AccessToken')) return;
      let session = await UploadApi.getUploadSession(fileIndex);
      let uploadId, key, uploadedParts = [];

      if (session && session.uploadId) {
        uploadId = session.uploadId;
        key = session.key;
        uploadedParts = session.parts || [];
      } else {
        if(sessionStorage.getItem('AccessToken') == null) return;
        const init = await UploadApi.initiateMultipartUpload({
          key: file.name,
          fileId: fileIndex,
        });
        uploadId = init.uploadId;
        key = init.key;
      }

      const uploadedPartNumbers = uploadedParts.map(e => e.PartNumber);
      let uploadPromises = [];

      let uploadedCount = uploadedParts.length; 

      for (let currentChunk = 0; currentChunk < totalChunks; currentChunk++) {
        const partNumber = currentChunk + 1;

        if (uploadedPartNumbers.includes(partNumber)) {
          continue; 
        }

        if (uploadedParts.some(p => p.PartNumber === partNumber)) {
          // commit('SET_FILE_PROGRESS', {
          //   fileId: fileIndex,
          //   // progress: Math.round(((currentChunk + 1) / totalChunks) * 100)
          //    progress: Math.round(((partNumber) / totalChunks) * 100)
          // });
          continue;
        }

        const start = currentChunk * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const fileChunk = file.slice(start, end);

        if(sessionStorage.getItem('AccessToken') == null) return;
        const presignedUrl = await UploadApi.getPresignedUrl(key, uploadId, partNumber, chunkSize);

        const uploadPromise = axios.put(presignedUrl, fileChunk, {
          headers: { 'Content-Type': file.type },
        }).then(async response => {
          const etag = response.headers['etag'] || response.headers['ETag'];

          if(sessionStorage.getItem('AccessToken') == null) return;
          await UploadApi.registerPart(key, uploadId, partNumber, etag);

          uploadedCount++; // 퍼센트 순차 증가 위한 flag
          commit('SET_FILE_PROGRESS', {
            fileId: fileIndex,
            // progress: Math.round(((currentChunk + 1) / totalChunks) * 100)
            // progress: Math.round(((partNumber) / totalChunks) * 100)
             progress: Math.round(((uploadedCount) / totalChunks) * 100)
          });

          return { PartNumber: partNumber, ETag: etag };
        }).catch(() => {
          isCompleted = false;
          commit('SET_FILE_STATUS', { fileId: fileIndex, status: 'failed' });
          dispatch('snackbar/showSnackbar', {
            message: `${file.name} ${ t('파일 업로드 실패')}`,
            error: true
          }, { root: true });
        });

        uploadPromises.push(uploadPromise);
      }

      const newParts = await Promise.all(uploadPromises);
      const allParts = [...uploadedParts, ...newParts].filter(Boolean);

      // 중복 PartNumber 제거
      const parts = allParts.reduce((acc, curr) => {
        if (!acc.some(p => p.PartNumber === curr.PartNumber)) acc.push(curr);
        return acc;
      }, []);

      if (isCompleted) {
        if(sessionStorage.getItem('AccessToken') == null) return;
        const res = await UploadApi.completeMultipartUpload(key, uploadId, parts, file.name, secure, aiVoice);
        // console.log("업로드 완료 => ", res) // 200상태코드리턴

        if(res == 200){
          commit('SET_FILE_PROGRESS', { fileId: fileIndex, progress: 100 });  // 100으로 설정 후 completed 설정해서 변경 확정시킴
          commit('SET_FILE_STATUS', { fileId: fileIndex, status: 'completed' }); 


          await removeFileData(fileIndex); // 완료된 파일 indexDB에서 삭제

          dispatch('snackbar/showSnackbar', {
            message: `${file.name} ${ t('파일 업로드 성공')}`,
            error: false
          }, { root: true });
        }
      }

    } catch (error) {
      console.error("Upload failed", error);
      commit('SET_FILE_STATUS', { fileId: fileIndex, status: 'failed' });
      dispatch('snackbar/showSnackbar', {
        message: `${file.name} ${ t('파일 업로드 실패')}`,
        error: true
      }, { root: true });
    }
  },

  // 재업로드 (이어받기 요청)
  async restoreUploads({ commit, dispatch }) {
    const storedFiles = await loadAllFileData();

    if (!Array.isArray(storedFiles)) {
      console.error('loadAllFileData returned non-array:', storedFiles);
      return;
    }

    const files = storedFiles.map(item => ({
      id: item.id,
      name: item.name,
      size: item.size,
      type: item.type,
      progress: item.progress,
      status: 'waitingToResume',
      file: new File([item.fileBlob], item.name, { type: item.type }),
      secure: item.secure || false,
      aiVoice: item.aiVoice || false,
    })); 
    
    // console.log("files => ", files)

    commit('SET_UPLOADED_FILES', files);
    dispatch('reUploadFiles');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
